import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';
import { scrollContainerToActiveChild, noop } from 'services/utils';
import { METRIC_KEYS, UNAVAILABLE_METRICS_RESPONSE } from 'services/api/DataConnectorService';
import { CREATE_DATACONNECTOR, UPDATE_DATACONNECTOR, DELETE_DATACONNECTOR } from 'services/Permissions';

const EVENT_KEYS = [
    'touch',
    'temperature',
    'contact',
    'objectPresent',
    'batteryStatus',
    'networkStatus',
    'labelsChanged',
    'connectionStatus',
    'ethernetStatus',
    'cellularStatus',
    'objectPresentCount',
    'touchCount',
    'humidity',
    'waterPresent',
    'co2',
    'pressure',
    'motion',
    'deskOccupancy',
    'virtualGlycolBuffer',
    'differentialPressure'
];

const EVENT_META_DATA = {
    touch: {
        description: 'When device is touched',
        label: 'All devices'
    },
    temperature: {
        description: 'Every periodic heartbeat & when touched',
        label: 'Temperature sensors'
    },
    contact: {
        description: 'When a door or window is opened or closed',
        label: 'Door & Window sensors'
    },
    objectPresent: {
        description: 'When an object in close proximity appears or disappears',
        label: 'Proximity sensors'
    },
    batteryStatus: {
        description: 'Approximately once per day',
        label: 'All sensors'
    },
    networkStatus: {
        description: 'Every periodic heartbeat or with other sensor events',
        label: 'All sensors'
    },
    labelsChanged: {
        description: 'When a device label is created, modified or removed',
        label: 'All devices'
    },
    connectionStatus: {
        description: 'When a Sensor or Cloud Connector changes its connection status, for example going offline.',
        label: 'All devices'
    },
    ethernetStatus: {
        description: 'When the ethernet connection changes',
        label: 'Cloud Connectors'
    },
    cellularStatus: {
        description: 'When the cellular connection changes',
        label: 'Cloud Connectors'
    },
    objectPresentCount: {
        description: 'Every periodic heartbeat',
        label: 'Proximity counting sensors'
    },
    touchCount: {
        description: 'Every periodic heartbeat',
        label: 'Counting touch sensors'
    },
    humidity: {
        description: 'Every periodic heartbeat (includes temperature)',
        label: 'Humidity sensors & CO2 sensors'
    },
    waterPresent: {
        description: 'When the sensor detects the appearance or disappearance of water',
        label: 'Water detector sensors'
    },
    co2: {
        description: 'Every periodic heartbeat',
        label: 'CO2 sensors'
    },
    pressure: {
        description: 'Every periodic heartbeat',
        label: 'CO2 sensors'
    },
    motion: {
        description: 'When motion is detected or no longer detected',
        label: 'Motion sensors'
    },
    deskOccupancy: {
        description: 'Every state change',
        label: 'Desk occupancy sensors'
    },
    virtualGlycolBuffer: {
        description: 'Every periodic heartbeat',
        label: 'Virtual Glycol'
    },
    differentialPressure: {
        description: 'Every periodic heartbeat',
        label: 'Differential pressure sensors'
    }
};

const FEATURE_FLAGGED_EVENTS = {
    virtualGlycolBuffer: 'sensor_virtual_glycol_buffer'
};

/* @ngInject */
export default class DataConnectorController {
    constructor(
        EventEmitter,
        $transitions,
        DialogService,
        $state,
        $timeout,
        DataConnectorService,
        RoleManager,
        ToastService,
        AnalyticsService,
        $scope,
        ClipboardService,
        FeatureFlags
    ) {
        this.EventEmitter = EventEmitter;
        this.$transitions = $transitions;
        this.DialogService = DialogService;
        this.$state = $state;
        this.$timeout = $timeout;
        this.DataConnectorService = DataConnectorService;
        this.toastService = ToastService;
        this.AnalyticsService = AnalyticsService;
        this.RoleManager = RoleManager;
        this.$scope = $scope;
        this.ClipboardService = ClipboardService;
        this.featureFlags = FeatureFlags;

        this.onExitGuard = this.onExitGuard.bind(this);
    }

    onExitGuard() {
        if (
            !_isEqual(_omit(this.originalDataConnector, METRIC_KEYS), _omit(this.dataConnector, METRIC_KEYS))
            || this.noEventsSelected
        ) {
            return this.DialogService.confirm({
                title: 'Data Connector was edited',
                textContent: 'Do you really want to leave? Changes will not be saved.',
                ariaLabel: 'drop-changes',
                ok: 'Leave',
                cancel: 'Stay'
            })
                .then(() => true)
                .catch(() => false);
        }
        return true;
    }

    $onInit() {
        this.originalDataConnector = _cloneDeep(this.dataConnector);

        this.unregisterExitGuard = this.$transitions.onExit({
            exiting: this.$state.current.name
        }, this.onExitGuard);

        this.eventMetaData = EVENT_META_DATA;
        this.buildActiveEvents();
        this.featureFlags.onLoaded().then(() => {
            this.buildActiveEvents();
            this.$scope.$applyAsync();
        });

        this.endpointUrlRegex = /^https:\/\//;
        this.serviceBusRegex = /^https:\/\/[a-zA-Z0-9-]+\.servicebus\.windows\.net\/[a-zA-Z0-9-]+$/;
        this.clientTenantIdRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        this.googlePubSubTopicRegex = /^projects\/\S+\/topics\/\S+$/;
        this.googlePubSubAudienceRegex = /^\/\/iam\.googleapis\.com\/projects\/\S+\/locations\/\S+\/workloadIdentityPools\/\S+\/providers\/\S+$/;
        this.awsSqsUrlRegex = /^https:\/\/sqs\.[a-zA-Z0-9-]+\.amazonaws\.com\/[0-9]{12}\/[a-zA-Z0-9-]+$/;
        this.awsRoleArnRegex = /^arn:aws:iam::[0-9]{12}:role\/[a-zA-Z0-9-]+$/;

        this.$timeout(scrollContainerToActiveChild);

        if (!this.isNew) {
            this.updateMetrics(true);
        }

        this.allEvents = !this.dataConnector.events.length;
        if (this.allEvents) {
            this.setAllEventsTo(true);
        }

        this.endpointUrlFieldName = 'endpointUrl';
        this.endpointUrlFieldEdited = false;
        this.azureUrlName = 'azureUrl';
        this.azureUrlFieldEdited = false;
        this.azureTenantIdName = 'tenantId';
        this.azureTenantIdFieldEdited = false;
        this.azureClientIdName = 'clientId';
        this.azureClientIdFieldEdited = false;
        this.googleTopicName = 'topic';
        this.googleTopicFieldEdited = false;
        this.googleAudienceName = 'googleAudience';
        this.googleAudienceFieldEdited = false;
        this.awsUrlName = 'awsUrl';
        this.awsUrlFieldEdited = false;
        this.awsRoleArnName = 'awsRoleArn';
        this.awsRoleArnFieldEdited = false;
        this.awsAudienceName = 'awsAudience';
        this.awsAudienceFieldEdited = false;

        this.showNoEventsSelectedError = false;

        this.isSynchronizing = false;

        this.showEventOptions = !this.allEvents
        this.showAzureServiceBusOptions = false

        this.currentPageSize = 25;
        this.currentPageToken = '';
        this.nextPageToken = null;
        this.fetchLogs();
    }

    fetchLogs() {
        this.DataConnectorService.dataConnectorLogs(this.dataConnector.name, this.currentPageSize, this.currentPageToken)
            .then(response => {
                this.logEvents = response.logs;
                this.nextPageToken = response.nextPageToken;
                if (this.logEvents.length > 0) {
                    this.selectedEvent = this.logEvents[0];
                } else {
                    this.selectedEvent = null;
                }
            })
            .catch(() => {
                this.logEvents = [];
                this.selectedEvent = null;
            })
            .finally(() => {
                this.$scope.$applyAsync();
            });
    }

    setPageToken({ pageToken }) {
        this.currentPageToken = pageToken || '';
        this.fetchLogs();
    }

    toggleShowEventOptions() {
        this.showEventOptions = !this.showEventOptions
        this.$scope.$applyAsync()
    }

    selectEvent(event) {
        this.selectedEvent = event;
    }

    setPageSize({ pageSize }) {
        this.currentPageSize = pageSize;
        this.currentPageToken = null;
        this.fetchLogs();
    }

    markEdited(fieldName = null) {
        if (!this.endpointUrlFieldEdited && fieldName === this.endpointUrlFieldName) {
            this.endpointUrlFieldEdited = true;
        }
        if (!this.azureUrlFieldEdited && fieldName === this.azureUrlName) {
            this.azureUrlFieldEdited = true;
        }
        if (!this.azureTenantIdFieldEdited && fieldName === this.azureTenantIdName) {
            this.azureTenantIdFieldEdited = true;
        }
        if (!this.azureClientIdFieldEdited && fieldName === this.azureClientIdName) {
            this.azureClientIdFieldEdited = true;
        }
        if (!this.googleTopicFieldEdited && fieldName === this.googleTopicName) {
            this.googleTopicFieldEdited = true;
        }
        if (!this.googleAudienceFieldEdited && fieldName === this.googleAudienceName) {
            this.googleAudienceFieldEdited = true;
        }
        if (!this.awsUrlFieldEdited && fieldName === this.awsUrlName) {
            this.awsUrlFieldEdited = true;
        }
        if (!this.awsRoleArnFieldEdited && fieldName === this.awsRoleArnName) {
            this.awsRoleArnFieldEdited = true;
        }
        if (!this.awsAudienceFieldEdited && fieldName === this.awsAudienceName) {
            this.awsAudienceFieldEdited = true;
        }

        this.$scope.$applyAsync();
    }

    $onDestroy() {
        this.unregisterExitGuard();
    }

    isSuccessStatusCode(statusCode) {
        return statusCode >= 200 && statusCode < 300;
    }

    httpStatusCodes = [
        // 1xx Informational
        { code: 100, message: "Continue" },
        { code: 101, message: "Switching Protocols" },
        { code: 102, message: "Processing" },
        { code: 103, message: "Early Hints" },

        // 2xx Success
        { code: 200, message: "OK" },
        { code: 201, message: "Created" },
        { code: 202, message: "Accepted" },
        { code: 203, message: "Non-Authoritative Information" },
        { code: 204, message: "No Content" },
        { code: 205, message: "Reset Content" },
        { code: 206, message: "Partial Content" },
        { code: 207, message: "Multi-Status" },
        { code: 208, message: "Already Reported" },
        { code: 226, message: "IM Used" },

        // 3xx Redirection
        { code: 300, message: "Multiple Choices" },
        { code: 301, message: "Moved Permanently" },
        { code: 302, message: "Found" },
        { code: 303, message: "See Other" },
        { code: 304, message: "Not Modified" },
        { code: 305, message: "Use Proxy" },
        { code: 307, message: "Temporary Redirect" },
        { code: 308, message: "Permanent Redirect" },

        // 4xx Client Error
        { code: 400, message: "Bad Request" },
        { code: 401, message: "Unauthorized" },
        { code: 402, message: "Payment Required" },
        { code: 403, message: "Forbidden" },
        { code: 404, message: "Not Found" },
        { code: 405, message: "Method Not Allowed" },
        { code: 406, message: "Not Acceptable" },
        { code: 407, message: "Proxy Authentication Required" },
        { code: 408, message: "Request Timeout" },
        { code: 409, message: "Conflict" },
        { code: 410, message: "Gone" },
        { code: 411, message: "Length Required" },
        { code: 412, message: "Precondition Failed" },
        { code: 413, message: "Payload Too Large" },
        { code: 414, message: "URI Too Long" },
        { code: 415, message: "Unsupported Media Type" },
        { code: 416, message: "Range Not Satisfiable" },
        { code: 417, message: "Expectation Failed" },
        { code: 418, message: "I'm a teapot" },
        { code: 421, message: "Misdirected Request" },
        { code: 422, message: "Unprocessable Entity" },
        { code: 423, message: "Locked" },
        { code: 424, message: "Failed Dependency" },
        { code: 425, message: "Too Early" },
        { code: 426, message: "Upgrade Required" },
        { code: 428, message: "Precondition Required" },
        { code: 429, message: "Too Many Requests" },
        { code: 431, message: "Request Header Fields Too Large" },
        { code: 451, message: "Unavailable For Legal Reasons" },

        // 5xx Server Error
        { code: 500, message: "Internal Server Error" },
        { code: 501, message: "Not Implemented" },
        { code: 502, message: "Bad Gateway" },
        { code: 503, message: "Service Unavailable" },
        { code: 504, message: "Gateway Timeout" },
        { code: 505, message: "HTTP Version Not Supported" },
        { code: 506, message: "Variant Also Negotiates" },
        { code: 507, message: "Insufficient Storage" },
        { code: 508, message: "Loop Detected" },
        { code: 510, message: "Not Extended" },
        { code: 511, message: "Network Authentication Required" }
    ];

    translateStatusCode(statusCode) {
        const statusCodeObject = this.httpStatusCodes.find(status => status.code === statusCode);
        if (statusCodeObject) {
            return statusCodeObject.message;
        }

        return statusCode;
    }

    buildActiveEvents() {
        this.activeEventKeys = EVENT_KEYS.filter(key => {
            const flag = FEATURE_FLAGGED_EVENTS[key];
            return !flag || this.featureFlags.isActive(flag);
        });
        const rowThreshold = Math.ceil(this.activeEventKeys.length / 2);
        this.eventKeyRows = [
            this.activeEventKeys.slice(0, rowThreshold),
            this.activeEventKeys.slice(rowThreshold)
        ];
        this.buildEventMap();
    }

    buildEventMap() {
        this.eventMap = this.activeEventKeys.reduce((acc, eventKey) => {
            acc[eventKey] = this.dataConnector.events.indexOf(eventKey) >= 0;
            return acc;
        }, {});
    }

    get canCreateNew() {
        return this.RoleManager.can(CREATE_DATACONNECTOR);
    }

    get canUpdate() {
        return this.RoleManager.can(UPDATE_DATACONNECTOR);
    }

    get canDelete() {
        return this.RoleManager.can(DELETE_DATACONNECTOR);
    }

    saveDataConnector() {
        if (this.noEventsSelected) {
            this.showNoEventsSelectedError = true;
            return;
        }
        if (this.dataConnector.status === 'SYSTEM_DISABLED') {
            this.dataConnector.status = 'USER_DISABLED';
        }

        this.AnalyticsService.trackEvent("dataconnector.updated")

        this.onUpdate(
            this.EventEmitter({
                dataConnector: this.dataConnector,
                patch: _omit(this.dataConnector, ['name', 'type', 'errorCount', 'latency99p', 'successCount'])
            })
        ).then(() => {
            this.originalDataConnector = _cloneDeep(this.dataConnector);
        });
    }

    updateLabels({ entities }) {
        this.dataConnector.labels = [...entities]; // break reference
    }

    setAllEventsTo(checked) {
        Object.keys(this.eventMap).forEach((key) => {
            this.eventMap[key] = checked;
        });
    }

    get noEventsSelected() {
        return !this.allEvents && !this.dataConnector.events.length;
    }

    onEventsChanged(allEventsToggled = false) {
        if (allEventsToggled) {
            this.setAllEventsTo(this.allEvents);
        }
        if (this.allEvents) {
            this.dataConnector.events = [];
        } else {
            this.showEventOptions = true
            this.dataConnector.events = this.activeEventKeys.filter(eventKey => this.eventMap[eventKey]);
        }
        if (this.showNoEventsSelectedError) {
            this.showNoEventsSelectedError = false;
        }
    }

    createDataConnector() {

        if (this.dataConnector.type === 'AZURE_SERVICE_BUS') {
            delete this.dataConnector.httpConfig;
        }

        if (this.dataConnector.type === 'HTTP_PUSH') {
            delete this.dataConnector.azureServiceBusConfig;
        }

        if (this.noEventsSelected) {
            this.showNoEventsSelectedError = true;
            return;
        }

        this.AnalyticsService.trackEvent("dataconnector.created")

        this.onCreate(
            this.EventEmitter({
                dataConnector: this.dataConnector
            })
        ).then(() => {
            this.originalDataConnector = _cloneDeep(this.dataConnector);
        });
    }

    deleteDataConnector() {
        this.onDelete(this.EventEmitter({
            dataConnector: this.originalDataConnector,
        }))
            .then(() => {
                this.dataConnector = _cloneDeep(this.originalDataConnector);
            })
            .catch(noop);
    }

    updateMetrics(quite = false) {
        this.AnalyticsService.trackEvent("dataconnector.metrics.refresh")

        const targetNode = document.querySelector('#metrics-refresh-icon');
        if (targetNode) {
            targetNode.classList.add('spinning');
        }
        this.DataConnectorService
            .dataConnectorMetrics(this.dataConnector.name)
            .then((response) => {
                Object.assign(this.dataConnector, response.metrics);
                this.onMetricsUpdate(
                    this.EventEmitter({
                        dataConnector: this.dataConnector,
                        metrics: response.metrics
                    })
                );
                if (!quite) {
                    this.toastService.showSimpleTranslated('dataconnector_metrics_was_refreshed');
                }
            })
            .catch((serverResponse) => {
                Object.assign(this.dataConnector, UNAVAILABLE_METRICS_RESPONSE.metrics);
                this.onMetricsUpdate(
                    this.EventEmitter({
                        dataConnector: this.dataConnector,
                        metrics: UNAVAILABLE_METRICS_RESPONSE.metrics
                    })
                );
                this.toastService.showSimpleTranslated('dataconnector_metrics_wasnt_refreshed', {
                    serverResponse
                });
            })
            .finally(() => {
                if (targetNode) {
                    targetNode.classList.remove('spinning');
                }
            });
    }

    showSynchronizeDialog() {
        if (this.isSynchronizing) {
            return;
        }
        this.isSynchronizing = true;
        this.DialogService.confirm({
            title: 'Synchronize Data Connector',
            textContent: 'Are you sure you want to start synchronizing the Data Connector?',
            ariaLabel: 'synchronize-data-connector',
            ok: 'Yes',
            cancel: 'Cancel'
        })
            .then(() => this.synchronizeDataConnector())
            .catch(() => false)
            .finally(() => {
                this.isSynchronizing = false;
            });
    }

    synchronizeDataConnector() {
        return this.DataConnectorService
            .synchronizeDataConnector(this.dataConnector.name)
            .then(() => {
                this.AnalyticsService.trackEvent("dataconnector.synchronization.started")
                this.toastService.showSimpleTranslated('dataconnector_sync_started');
            })
            .catch((serverResponse) => {
                let toastResponse = '';
                switch (serverResponse.status) {
                    case 409:
                        this.AnalyticsService.trackEvent("dataconnector.synchronization.already_in_progress")
                        toastResponse = 'dataconnector_sync_already_in_progress';
                        break;
                    default:
                        this.AnalyticsService.trackEvent("dataconnector.synchronization.failed")
                        toastResponse = 'dataconnector_sync_failed';
                }
                this.toastService.showSimpleTranslated(toastResponse, { serverResponse });
            });
    }

    typeDisplayName(type) {
        switch (type) {
            case 'HTTP_PUSH':
                return 'Webhook';
            case 'AZURE_SERVICE_BUS':
                return 'Azure Service Bus';
            case 'AZURE_EVENT_HUB':
                return 'Azure Event Hub';
            case 'GOOGLE_CLOUD_PUBSUB':
                return 'Google Pub/Sub';
            case 'AWS_SQS':
                return 'AWS SQS';
            default:
                return type;
        }
    }

    copyResponseBody() {
        this.ClipboardService.copyStringToClipboard(this.selectedEvent.responseBody, 'Response body');
    }
}
